import {
  OreButton,
  OreCardContainer,
  OreCardWrapper,
  OreDivider,
  OreHeading,
  OreStack,
  OreText,
} from '@edenredespana/oreneta'

import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { LoginTranslation } from '@/Account/ui/accountTranslation'

import { createPasswordRecoveryRepository } from '@/Account/infrastructure/ApiPasswordRecoveryRepository'
import { ResetPasswordData, usePasswordRecoveryController } from '@/Account/ui/hooks'
import { PasswordResetBasicForm, PasswordResetExpiredToken } from '@/Account/ui/molecules'
import { formTranslation } from '@/Shared/ui/sharedTranslation'
import { useLoader } from '@/Shared/ui/loader'

export const PasswordResetForm = (): JSX.Element => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const { t } = useTranslation()
  const { resetPasswordForm, updatePassword, validateToken } = usePasswordRecoveryController(createPasswordRecoveryRepository())
  const [isValidating, setIsValidating] = useState<boolean>(true)
  const { formState: { isValid }, watch } = resetPasswordForm

  const decodeJWT = (token: string) => {
    try {
      const payload = token.split('.')[1]
      const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
      return JSON.parse(decodedPayload)
    } catch (error) {
      console.error('Error decoding JWT:', error)
      return null
    }
  }

  const {
    exp,
    username
  } = decodeJWT(token ?? '')

  const { startLoading, stopLoading } = useLoader()

  const handleSubmit = async (data: ResetPasswordData) => {
    startLoading()
    const response = await updatePassword(data, username)
    stopLoading()
  }

  const [isValidToken, setIsValidToken] = useState(false)
  useEffect(() => {
    const fetchValidation = async () => {
      setIsValidating(true)
      const response = await validateToken({ token, userName: username })
      setIsValidToken(response)
      setIsValidating(false)
    }
    fetchValidation()
  }, [token])

  return (
    <>
      {!isValidating && (
        isValidToken ? (
          <FormProvider {...resetPasswordForm}>
            <OreStack>
              <OreStack space="small" placeContent="stretch" placeItems="start">
                <OreHeading as="h3" size="headline-lg">
                  {t(LoginTranslation.recovery.form.resetPassword.title)}
                </OreHeading>
                <OreDivider />
              </OreStack>
              <form>
                <OreCardContainer>
                  <OreCardWrapper space="large">
                    <OreStack space="medium" placeContent="stretch" placeItems="start" style={{ width: '40rem' }}>
                      <OreStack space="xsmall" placeContent="stretch">
                        <OreText size="text-md" tone="neutral-600">
                          {t(LoginTranslation.recovery.form.resetPassword.usernameLabel)}
                        </OreText>
                        <OreHeading size="headline-md">
                          {username}
                        </OreHeading>
                      </OreStack>
                      <PasswordResetBasicForm token={token} />
                    </OreStack>
                  </OreCardWrapper>
                </OreCardContainer>

                <OreDivider space="medium" />

                <OreStack space="large" placeContent="end" placeItems="center" direction="row">
                  <OreButton
                    disabled={!isValid}
                    type="submit"
                    onClick={resetPasswordForm.handleSubmit(handleSubmit)}
                    size="large"
                    category="primary"
                    data-testid="continue"
                  >
                    {t(formTranslation.common.buttons.accept)}
                  </OreButton>
                </OreStack>
              </form>
            </OreStack>
          </FormProvider>
        ) : (
          <PasswordResetExpiredToken />
        )
      )}
    </>
  )
}
